import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { getHarvests, getAllApiariesService, deleteHarvest } from '../../../../services/apiaryService';
import Swal from 'sweetalert2';
import close from '../assets/images/close.png';
import * as Styles from '../Farmers/components/FarmerForm/style';
import { Row, Col, Button, Form } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import BounceLoader from 'react-spinners/BounceLoader';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Select, DatePicker, Empty, Tag, Input } from 'antd';
import moment from 'moment';
import { Link } from 'react-router-dom';
import LocationResolver from '../Apiaries/LocationResolver';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import GenericExportCSVModal from '../shared/GenericExportCSVModal';

const { Option } = Select;

// --- Styled Components ---

const MainContainer = styled.div`
    padding: 30px;
    background: #fcfcfc;
    min-height: 100vh;
    margin-left: 15%;
    width: 85%;
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const SummaryCards = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 24px;
    margin-bottom: 30px;
`;

const StatCard = styled.div`
    background: linear-gradient(135deg, ${props => props.color || '#ff8701'} 0%, ${props => props.color2 || '#ffbc6d'} 100%);
    padding: 24px;
    border-radius: 20px;
    color: white;
    box-shadow: 0 10px 20px rgba(0,0,0,0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 15px 30px rgba(0,0,0,0.12);
    }

    &::after {
        content: '';
        position: absolute;
        top: -50%;
        right: -50%;
        width: 150px;
        height: 150px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
    }

    .label {
        font-size: 14px;
        font-weight: 600;
        opacity: 0.9;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        margin-bottom: 12px;
    }

    .value {
        font-size: 32px;
        font-weight: 800;
        margin: 0;
        line-height: 1;
    }

    .sub-value {
        font-size: 13px;
        margin-top: 8px;
        opacity: 0.85;
    }
`;

const FilterCard = styled.div`
    background: #fff;
    padding: 24px;
    border-radius: 16px;
    margin-bottom: 30px;
    border: 1px solid #f0f0f0;
    box-shadow: 0 4px 20px rgba(0,0,0,0.03);
`;

const TableWrapper = styled.div`
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.04);
    overflow: hidden;
    border: 1px solid #f0f0f0;

    .modern-table thead th {
        background: #fafafa;
        color: #4a4a4a;
        font-weight: 700;
        font-size: 12px;
        padding: 20px;
        text-transform: uppercase;
        border-bottom: 2px solid #ffcc00 !important;
        letter-spacing: 0.5px;
    }

    .modern-table tbody td {
        padding: 20px;
        vertical-align: middle;
        font-size: 14px;
        border-top: 1px solid #f8f8f8;
        color: #2d3436;
    }

    .modern-table tbody tr:hover {
        background-color: #fffdf5 !important;
    }
`;

const GradeBadge = styled.span`
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    background: ${props => {
        if (props.grade?.startsWith('A')) return '#e6fffa';
        if (props.grade?.startsWith('B')) return '#fffbe6';
        return '#f9f9f9';
    }};
    color: ${props => {
        if (props.grade?.startsWith('A')) return '#00b894';
        if (props.grade?.startsWith('B')) return '#d4a017';
        return '#636e72';
    }};
    border: 1px solid ${props => {
        if (props.grade?.startsWith('A')) return '#b2f2bb';
        if (props.grade?.startsWith('B')) return '#ffe58f';
        return '#d9d9d9';
    }};
`;

const SensoryTag = styled(Tag)`
    border-radius: 6px;
    font-size: 11px;
    margin-bottom: 6px;
    font-weight: 600;
    padding: 2px 10px;
`;

// --- Detail Modal (Reusing from NectarHarvests) ---
// Note: In a real project, this should be moved to a shared component file.
const ModalSection = styled.div`
    margin-bottom: 35px;

    h5 {
        font-size: 16px;
        font-weight: 700;
        color: #2d3436;
        border-left: 5px solid #ffcc00;
        padding-left: 14px;
        margin-bottom: 24px;
        line-height: 1.2;
    }
`;

const MetricGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
`;

const MetricItem = styled.div`
    background: #f8f9fa;
    padding: 16px;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px solid #f1f2f6;
    transition: all 0.2s ease;

    &:hover {
        background: #fff;
        border-color: #ffcc00;
    }

    .m-label {
        font-size: 11px;
        color: #b2bec3;
        font-weight: 700;
        text-transform: uppercase;
        margin-bottom: 8px;
        letter-spacing: 0.5px;
    }
    .m-value {
        font-size: 20px;
        font-weight: 700;
        color: #2d3436;
    }
    .m-unit {
        font-size: 11px;
        color: #636e72;
        margin-top: 4px;
    }
`;

const PhotoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
    margin-top: 15px;
`;

const PhotoItem = styled.div`
    aspect-ratio: 4/3;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid #f0f0f0;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.03);
        border-color: #ffcc00;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;
const HarvestDetailModal = ({ data, onClose }) => {
    if (!data) return null;

    let flowerName = '';
    if (data.selected_flower) {
        if (typeof data.selected_flower === 'object') {
            flowerName = data.selected_flower.localName || data.selected_flower.plant || '';
        } else if (typeof data.selected_flower === 'string') {
            flowerName = data.selected_flower;
        }
    }

    const sections = [
        {
            title: 'Harvest Overview',
            metrics: [
                { label: 'Weight', value: data.total_honey_kg, unit: 'KG' },
                { label: 'Hives count', value: data.hives_count, unit: 'Hives' },
                { label: 'Super Frames', value: data.super_frames, unit: 'Frames' },
                { label: 'Brood Frames', value: data.brood_frames, unit: 'Frames' },
                { label: 'Sealed', value: data.frames_wax_sealed, unit: '' },
                { label: 'Flower Type', value: data.flowering_type, unit: '' },
                { label: 'Flower', value: flowerName || '-', unit: '' }
            ]
        },
        {
            title: 'Honey Quality & Environment',
            metrics: [
                { label: 'Brix Content', value: data.brix_value, unit: '%' },
                { label: 'Moisture', value: data.moisture_content, unit: '%' },
                { label: 'Temperature', value: data.harvest_temperature, unit: '°C' },
                { label: 'Humidity', value: data.harvest_humidity, unit: '%' },
                { label: 'Grade', value: data.grade, unit: '' }
            ]
        }
    ];

    const sensoryProfile = [
        { label: 'Aroma', values: data.aroma_profile, color: 'orange' },
        { label: 'Texture', values: data.texture, color: 'gold' },
        { label: 'Taste', values: data.taste, color: 'volcano' },
        { label: 'Colour', values: [data.colour], color: 'brown' }
    ];

    const extractionProtocol = data.extraction_protocol || {};

    return (
        <Styles.ModalWrapper style={{ zIndex: 1060 }}>
            <Styles.ModalContent2 style={{ maxWidth: '900px', width: '95%', borderRadius: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '30px 30px 0 30px' }}>
                    <div>
                        <h3 style={{ margin: 0, color: '#f39c12', fontSize: '24px', fontWeight: 800 }}>Production Log Details</h3>
                        <p style={{ margin: 0, color: '#b2bec3', fontSize: '14px', fontWeight: 500 }}>
                            <span style={{ color: '#2d3436', fontWeight: 700 }}>{data.harvest_id}</span> | {moment(data.harvested_on).format('DD MMMM YYYY')}
                        </p>
                    </div>
                    <img src={close} alt="Close" onClick={onClose} style={{ width: 22, cursor: 'pointer', opacity: 0.6 }} />
                </div>

                <div style={{ padding: '30px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <SummaryCards style={{ marginBottom: '30px' }}>
                        <StatCard color="#f39c12" color2="#ffcc00">
                            <div className="label">Quality Benchmark</div>
                            <div className="value">{data.grade || 'N/A'}</div>
                            <div className="sub-value">{data.crystallization || 'Optimal liquid state'}</div>
                        </StatCard>
                        <StatCard color="#e67e22" color2="#f39c12">
                            <div className="label">Harvest Quantity</div>
                            <div className="value">{data.total_honey_kg} <span style={{ fontSize: '18px' }}>kg</span></div>
                            <div className="sub-value">{data.hives_count} hives processed in this session</div>
                        </StatCard>
                        <StatCard color="#2d3436" color2="#636e72">
                            <div className="label">Apiary Origin</div>
                            <div className="value" style={{ fontSize: '18px' }}>{data.apiary_name || 'N/A'}</div>
                            <div className="sub-value">Location ID: {data.apiary_id || 'N/A'}</div>
                        </StatCard>
                    </SummaryCards>

                    {sections.map((section, idx) => (
                        <ModalSection key={idx}>
                            <h5>{section.title}</h5>
                            <MetricGrid>
                                {section.metrics.map((m, i) => (
                                    <MetricItem key={i}>
                                        <div className="m-label">{m.label}</div>
                                        <div className="m-value">{m.value ?? 'N/A'}</div>
                                        <div className="m-unit">{m.unit}</div>
                                    </MetricItem>
                                ))}
                            </MetricGrid>
                        </ModalSection>
                    ))}

                    <Row>
                        <Col md={7}>
                            <ModalSection>
                                <h5>Colony Health Snapshot</h5>
                                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '16px', border: '1px solid #eee' }}>
                                    {data.colony_health_snapshot && data.colony_health_snapshot.length > 0 ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                            {data.colony_health_snapshot.map((tag, i) => (
                                                <Tag key={i} color="green" style={{ borderRadius: '6px', margin: 0, fontWeight: 600 }}>{tag}</Tag>
                                            ))}
                                        </div>
                                    ) : <span style={{ fontSize: '13px', color: '#ccc' }}>No health markers recorded</span>}
                                    <div style={{ marginTop: '20px', display: 'flex', gap: '30px' }}>
                                        <div>
                                            <div style={{ fontSize: '11px', color: '#b2bec3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Colony Strength</div>
                                            <div style={{ fontWeight: 700, fontSize: '15px', color: '#2d3436' }}>{data.colony_strength || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '11px', color: '#b2bec3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Supers in Hive</div>
                                            <div style={{ fontWeight: 700, fontSize: '15px', color: '#2d3436' }}>{data.supers_in_hive || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            </ModalSection>
                        </Col>
                        <Col md={5}>
                            <ModalSection>
                                <h5>Extraction Protocol</h5>
                                <div style={{ background: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #eee', fontSize: '13px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#636e72' }}>Closed Space:</span>
                                        <strong style={{ color: data.extraction_protocol?.closed_space ? '#00b894' : '#ff7675' }}>{extractionProtocol.closed_space ? 'YES' : 'NO'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#636e72' }}>SS Extractor:</span>
                                        <strong style={{ color: data.extraction_protocol?.ss_extractor ? '#00b894' : '#ff7675' }}>{extractionProtocol.ss_extractor ? 'YES' : 'NO'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#636e72' }}>Gloves Worn:</span>
                                        <strong style={{ color: data.extraction_protocol?.gloves_worn ? '#00b894' : '#ff7675' }}>{extractionProtocol.gloves_worn ? 'YES' : 'NO'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#636e72' }}>Time Window:</span>
                                        <strong style={{ color: '#2d3436' }}>{extractionProtocol.start_time || 'N/A'} - {extractionProtocol.end_time || 'N/A'}</strong>
                                    </div>
                                </div>
                            </ModalSection>
                        </Col>
                    </Row>

                    <ModalSection>
                        <h5>Sensory Profile</h5>
                        <div style={{ background: '#fcfcfc', padding: '24px', borderRadius: '16px', border: '1px solid #f0f0f0' }}>
                            <Row>
                                {sensoryProfile.map((profile, i) => (
                                    <Col md={3} sm={6} key={i} className="mb-3">
                                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#b2bec3', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{profile.label}</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {profile.values && profile.values.length > 0 ? (
                                                profile.values.map((val, j) => val && <SensoryTag key={j} color={profile.color}>{val.replace(/_/g, ' ')}</SensoryTag>)
                                            ) : (
                                                <span style={{ fontSize: '13px', color: '#ccc' }}>N/A</span>
                                            )}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </ModalSection>

                    {((data.harvest_photos && data.harvest_photos.length > 0) || (data.brix_photos && data.brix_photos.length > 0)) && (
                        <ModalSection>
                            <h5>Quality Inspection Photos</h5>
                            <Row>
                                <Col md={6}>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#2d3436', marginBottom: '12px' }}>Harvesting Site</div>
                                    {data.harvest_photos && data.harvest_photos.length > 0 ? (
                                        <PhotoGrid>
                                            {data.harvest_photos.map((p, i) => (
                                                <PhotoItem key={i} onClick={() => window.open(p, '_blank')}>
                                                    <img src={p} alt="Harvest" onError={(e) => { e.target.src = 'https://placehold.co/400x300?text=Image+Error'; }} />
                                                </PhotoItem>
                                            ))}
                                        </PhotoGrid>
                                    ) : <p style={{ fontSize: '13px', color: '#ccc' }}>No harvest site photos available</p>}
                                </Col>
                                <Col md={6}>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#2d3436', marginBottom: '12px' }}>Refractometer / Brix Check</div>
                                    {data.brix_photos && data.brix_photos.length > 0 ? (
                                        <PhotoGrid>
                                            {data.brix_photos.map((p, i) => (
                                                <PhotoItem key={i} onClick={() => window.open(p, '_blank')}>
                                                    <img src={p} alt="Brix Check" onError={(e) => { e.target.src = 'https://placehold.co/400x300?text=Image+Error'; }} />
                                                </PhotoItem>
                                            ))}
                                        </PhotoGrid>
                                    ) : <p style={{ fontSize: '13px', color: '#ccc' }}>No Brix check photos available</p>}
                                </Col>
                            </Row>
                        </ModalSection>
                    )}

                    <ModalSection className="mb-0">
                        <h5>Storage & Sign-off</h5>
                        <div style={{ background: '#fff9f0', border: '1px solid #ffe8d6', padding: '24px', borderRadius: '16px' }}>
                            <Row>
                                <Col md={4}>
                                    <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#b2bec3' }}><strong>Container:</strong> <span style={{ color: '#2d3436', fontWeight: 600 }}>{(data.volume_storage && data.volume_storage.storage_container) || 'N/A'}</span></p>
                                    <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#b2bec3' }}><strong>Labelled:</strong> <span style={{ color: '#2d3436', fontWeight: 600 }}>{(data.volume_storage && data.volume_storage.labelled) ? 'YES' : 'NO'}</span></p>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#b2bec3' }}><strong>Strained:</strong> <span style={{ color: '#2d3436', fontWeight: 600 }}>{(data.volume_storage && data.volume_storage.strained) ? 'YES' : 'NO'}</span></p>
                                </Col>
                                <Col md={8}>
                                    <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 800, color: '#ff8701', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Supervisor Remarks</p>
                                    <p style={{ margin: 0, fontSize: '15px', color: '#2d3436', fontWeight: 500, lineHeight: '1.6' }}>{data.supervisor_remarks || (data.sign_off && data.sign_off.supervisor_remarks) || 'The supervisor has confirmed the quality standards for this batch.'}</p>
                                    {data.additional_remarks && (
                                        <p style={{ margin: '15px 0 0', fontSize: '13px', color: '#636e72', fontStyle: 'italic', background: '#fdf2e9', padding: '10px', borderRadius: '8px' }}>Note: {data.additional_remarks}</p>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    </ModalSection>
                </div>
            </Styles.ModalContent2>
        </Styles.ModalWrapper>
    );
};

// --- Main Component ---

const HarvestsTable = () => {
    const [harvests, setHarvests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [apiaries, setApiaries] = useState([]);
    const [page, setPage] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(1000);
    const [showExportModal, setShowExportModal] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        bee_species: '',
        harvest_method: '',
        dateRange: null
    });

    useEffect(() => {
        getAllApiariesService().then(data => setApiaries(data || []));
    }, []);

    const fetchHarvestsData = async () => {
        setLoading(true);
        try {
            const apiFilters = {};
            if (filters.bee_species) apiFilters.bee_species = filters.bee_species;
            if (filters.harvest_method) apiFilters.harvest_method = filters.harvest_method;
            if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                apiFilters.date_from = filters.dateRange[0].format('YYYY-MM-DD');
                apiFilters.date_to = filters.dateRange[1].format('YYYY-MM-DD');
            }

            const data = await getHarvests(null, apiFilters); // Passing null for all apiaries
            setHarvests(data || []);
        } catch (error) {
            console.error('Failed to load harvests:', error);
            setHarvests([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHarvestsData();
    }, [filters.bee_species, filters.harvest_method, filters.dateRange]);

    const handleDeleteClick = async (harvestId) => {
        const result = await Swal.fire({
            title: 'Delete Harvest Log',
            text: 'Are you sure you want to permanently delete this harvest record? This action cannot be undone.',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d63031',
            cancelButtonColor: '#636e72',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.value) {
            Swal.fire({
                title: 'Deleting...',
                allowOutsideClick: false,
                onBeforeOpen: () => {
                    Swal.showLoading();
                }
            });

            try {
                await deleteHarvest(harvestId);
                await Swal.fire({
                    title: 'Deleted!',
                    text: 'Harvest log has been deleted successfully.',
                    type: 'success',
                    confirmButtonColor: '#ff8701'
                });
                // Remove from local state
                setHarvests(prev => prev.filter(h => h.harvest_id !== harvestId));
            } catch (error) {
                console.error("Error deleting harvest:", error);
                await Swal.fire({
                    title: 'Error',
                    text: error.response?.data?.error || 'Failed to delete harvest log. Please try again.',
                    type: 'error',
                    confirmButtonColor: '#ff8701'
                });
            }
        }
    };

    const filteredHarvests = useMemo(() => {
        const mapped = harvests.map(h => {
            const apiary = apiaries.find(a => a.apiary_id === h.apiary_id);
            const floweringType = h.flowering_type || (apiary ? apiary.flowering_type : '') || '-';

            // Prefer the harvest's own selected_flower field (API sends it directly),
            // fall back to the apiary's selected_flower only if harvest doesn't have it
            const selectedFlower = h.selected_flower || (apiary ? apiary.selected_flower : null);

            let flowerName = '';
            if (selectedFlower) {
                if (Array.isArray(selectedFlower)) {
                    // Multifloral: array of flower names
                    flowerName = selectedFlower
                        .map(f => (typeof f === 'object' ? (f.localName || f.plant || '') : f))
                        .filter(Boolean)
                        .join(', ');
                } else if (typeof selectedFlower === 'object') {
                    flowerName = selectedFlower.localName || selectedFlower.plant || '';
                } else if (typeof selectedFlower === 'string') {
                    flowerName = selectedFlower;
                }
            }

            let honeyType = floweringType || '-';
            if (floweringType && floweringType.toLowerCase() === 'monofloral') {
                honeyType = flowerName ? `Monofloral (${flowerName})` : 'Monofloral';
            } else if (floweringType && floweringType.toLowerCase() === 'multifloral') {
                honeyType = 'Multifloral';
            }

            return {
                ...h,
                apiary_name: apiary ? apiary.apiary_name : (h.apiary_name || 'Unknown Apiary'),
                state: h.state || (apiary ? apiary.state : '') || '-',
                district: h.district || (apiary ? apiary.district : '') || '-',
                flowering_type: floweringType,
                selected_flower_name: flowerName,
                honey_type: honeyType
            };
        });
        if (!searchTerm) return mapped;
        return mapped.filter(h =>
            h.harvest_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            h.apiary_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            h.apiary_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            h.selected_flower_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            h.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            h.district?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            h.honey_type?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [harvests, searchTerm, apiaries]);

    const summaryStats = useMemo(() => {
        const total = filteredHarvests.reduce((acc, h) => acc + (h.total_honey_kg || 0), 0);
        const avgBrix = filteredHarvests.length > 0
            ? filteredHarvests.reduce((acc, h) => acc + (h.brix_value || 0), 0) / filteredHarvests.length
            : 0;
        const totalHives = filteredHarvests.reduce((acc, h) => acc + (h.hives_count || 0), 0);

        return {
            total: total.toFixed(1),
            avgBrix: avgBrix.toFixed(1),
            totalHives,
            count: filteredHarvests.length
        };
    }, [filteredHarvests]);

    const paginatedHarvests = useMemo(() => {
        const start = (page - 1) * sizePerPage;
        return filteredHarvests.slice(start, start + sizePerPage);
    }, [filteredHarvests, page, sizePerPage]);

    const totalPages = Math.ceil(filteredHarvests.length / sizePerPage) || 1;

    const EXPORT_COLUMNS = [
        { label: 'Harvest ID', key: 'harvest_id', path: 'harvest_id' },
        { label: 'Apiary Origin', key: 'apiary_name', path: 'apiary_name' },
        { label: 'Apiary ID', key: 'apiary_id', path: 'apiary_id' },
        { label: 'Date', key: 'harvested_on', formatter: (row) => row.harvested_on ? moment(row.harvested_on).format('DD MMM YYYY') : '' },
        { label: 'State', key: 'state', path: 'state' },
        { label: 'District', key: 'district', path: 'district' },
        { label: 'Flora Type', key: 'flowering_type', path: 'flowering_type' },
        { label: 'Flower', key: 'selected_flower_name', path: 'selected_flower_name' },
        { label: 'Quantity (kg)', key: 'total_honey_kg', path: 'total_honey_kg' },
        { label: 'Quality (Brix %)', key: 'brix_value', path: 'brix_value' },
        { label: 'Grade', key: 'grade', path: 'grade' },
        { label: 'Hives Count', key: 'hives_count', path: 'hives_count' }
    ];

    const columns = [
        /* {
            dataField: 'harvest_id',
            text: 'Harvest ID',
            sort: true,
            formatter: (cell, row) => (
                <div onClick={() => setSelectedItem(row)} style={{ cursor: 'pointer', color: '#ff8701', fontWeight: 700 }}>
                    {cell}
                </div>
            )
        }, */
        {
            dataField: 'apiary_name',
            text: 'Apiary Origin',
            sort: true,
            formatter: (cell, row) => (
                <div onClick={() => setSelectedItem(row)} style={{ cursor: 'pointer' }}>
                    <div style={{ fontWeight: 700, color: '#ff8701' }}>{cell || 'Unknown Apiary'}</div>
                    <div style={{ fontSize: '11px', color: '#b2bec3' }}>ID: {row.apiary_id}</div>
                    <Link to={`/nector-apiaries/${row.apiary_id}`} style={{ fontSize: '11px', color: '#ff8701', fontWeight: 600 }}>Go to Apiary →</Link>
                </div>
            )
        },
        {
            dataField: 'harvested_on',
            text: 'Date',
            sort: true,
            formatter: (cell) => moment(cell).format('DD MMM YYYY'),
            style: { fontWeight: '600' }
        },
        {
            dataField: 'state',
            text: 'State',
            sort: true,
            formatter: (cell, row) => {
                const apiary = apiaries.find(a => a.apiary_id === row.apiary_id);
                return (
                    <LocationResolver
                        lat={apiary?.geo_location?.lat || apiary?.latitude}
                        lng={apiary?.geo_location?.lng || apiary?.longitude}
                        value={cell}
                        field="state"
                        apiaryId={row.apiary_id}
                    />
                );
            },
            style: { fontWeight: '600' }
        },
        {
            dataField: 'district',
            text: 'District',
            sort: true,
            formatter: (cell, row) => {
                const apiary = apiaries.find(a => a.apiary_id === row.apiary_id);
                return (
                    <LocationResolver
                        lat={apiary?.geo_location?.lat || apiary?.latitude}
                        lng={apiary?.geo_location?.lng || apiary?.longitude}
                        value={cell}
                        field="district"
                        apiaryId={row.apiary_id}
                    />
                );
            },
            style: { fontWeight: '600' }
        },
        {
            dataField: 'flowering_type',
            text: 'Flora Type',
            sort: true,
            formatter: (cell, row) => (
                <span style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 700,
                    background: cell === 'Multifloral' ? '#fff3e0' : '#e8f5e9',
                    color: cell === 'Multifloral' ? '#e67e22' : '#27ae60',
                    border: `1px solid ${cell === 'Multifloral' ? '#f39c12' : '#81c784'}`
                }}>
                    {cell || '-'}
                </span>
            ),
            style: { fontWeight: '600' }
        },
        {
            dataField: 'selected_flower_name',
            text: 'Flower',
            sort: true,
            formatter: (cell) => cell ? (
                <span style={{ fontStyle: 'italic', color: '#2d3436', fontWeight: 600 }}>{cell}</span>
            ) : <span style={{ color: '#b2bec3' }}>—</span>,
            style: { fontWeight: '600' }
        },
        {
            dataField: 'total_honey_kg',
            text: 'Quantity',
            sort: true,
            formatter: (cell) => <span style={{ color: '#e67e22', fontWeight: 800 }}>{cell} kg</span>
        },
        {
            dataField: 'brix_value',
            text: 'Quality (Brix)',
            sort: true,
            formatter: (cell) => <span style={{ color: '#f39c12', fontWeight: 700 }}>{cell || '-'}%</span>,
            style: { textAlign: 'center' },
            headerStyle: { textAlign: 'center' }
        },
        {
            dataField: 'grade',
            text: 'Grade',
            formatter: (cell) => <GradeBadge grade={cell}>{cell || 'N/A'}</GradeBadge>,
            style: { textAlign: 'center' },
            headerStyle: { textAlign: 'center' }
        },
        {
            dataField: 'view',
            text: 'Action',
            formatter: (_, row) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => setSelectedItem(row)}
                        style={{ color: '#ff8701', textDecoration: 'none', fontWeight: 700, background: '#fff9f0', padding: '5px 12px', borderRadius: '6px' }}
                    >
                        View Info
                    </Button>
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleDeleteClick(row.harvest_id)}
                        style={{ color: '#d63031', textDecoration: 'none', fontWeight: 700, background: '#fff5f5', padding: '5px 12px', borderRadius: '6px' }}
                    >
                        Delete
                    </Button>
                </div>
            ),
            headerStyle: { width: '190px' }
        }
    ];

    const resetFilters = () => {
        setFilters({ bee_species: '', harvest_method: '', dateRange: null });
        setSearchTerm('');
    };

    return (
        <MainContainer>
            <HeaderSection>
                <div>
                    <h2 style={{ margin: 0, color: '#2d3436', fontWeight: 800, fontSize: '28px' }}>Production Harvests</h2>
                    <p style={{ margin: '5px 0 0', color: '#b2bec3', fontSize: '15px' }}>Tracking and managing field honey extractions across all apiaries.</p>
                </div>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <Input.Search
                        placeholder="Search by ID, Apiary or Flower..."
                        style={{ width: 300 }}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        allowClear
                    />
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowExportModal(true)}
                        style={{ fontWeight: 700, borderRadius: '8px' }}
                    >
                        <i className="fa fa-download mr-2"></i> Export CSV
                    </Button>
                    <Tag color="orange" style={{ padding: '5px 15px', borderRadius: '20px', fontWeight: 700, fontSize: '14px', border: 'none', display: 'flex', alignItems: 'center' }}>
                        {harvests.length} Total Logs
                    </Tag>
                </div>
            </HeaderSection>

            <SummaryCards>
                <StatCard color="#f39c12" color2="#ffcc00">
                    <div className="label">Gross Honey Yield</div>
                    <div className="value">{summaryStats.total} <span style={{ fontSize: '18px' }}>kg</span></div>
                    <div className="sub-value">Combined production across filtered logs</div>
                </StatCard>
                <StatCard color="#e67e22" color2="#f39c12">
                    <div className="label">Avg Quality Level</div>
                    <div className="value">{summaryStats.avgBrix}<span style={{ fontSize: '18px' }}>%</span></div>
                    <div className="sub-value">Average Brix content (Honey Purity)</div>
                </StatCard>
                <StatCard color="#2d3436" color2="#636e72">
                    <div className="label">Total Extractions</div>
                    <div className="value">{summaryStats.count}</div>
                    <div className="sub-value">Sessions logged in selected period</div>
                </StatCard>
            </SummaryCards>

            <FilterCard>
                <Row className="align-items-end">
                    <Col md={3}>
                        <Form.Label style={{ fontSize: '11px', fontWeight: 800, color: '#b2bec3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bee Species</Form.Label>
                        <Select
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="Filter by Species"
                            value={filters.bee_species || undefined}
                            onChange={v => setFilters({ ...filters, bee_species: v })}
                            allowClear
                        >
                            <Option value="Apis mellifera">Apis Mellifera</Option>
                            <Option value="Apis cerana">Apis Cerana</Option>
                        </Select>
                    </Col>
                    <Col md={3}>
                        <Form.Label style={{ fontSize: '11px', fontWeight: 800, color: '#b2bec3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Extraction Method</Form.Label>
                        <Select
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="Filter by Method"
                            value={filters.harvest_method || undefined}
                            onChange={v => setFilters({ ...filters, harvest_method: v })}
                            allowClear
                        >
                            <Option value="sealed_super">Sealed Super</Option>
                            <Option value="unsealed_partial">Unsealed Partial</Option>
                            <Option value="wild_harvest">Wild Harvest</Option>
                            <Option value="stingless">Stingless</Option>
                        </Select>
                    </Col>
                    <Col md={4}>
                        <Form.Label style={{ fontSize: '11px', fontWeight: 800, color: '#b2bec3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Time Period</Form.Label>
                        <DatePicker.RangePicker
                            style={{ width: '100%' }}
                            size="large"
                            value={filters.dateRange}
                            onChange={dates => setFilters({ ...filters, dateRange: dates })}
                        />
                    </Col>
                    <Col md={2}>
                        <Button
                            variant="secondary"
                            style={{ width: '100%', height: '40px', fontWeight: 700, borderRadius: '8px', background: '#f1f2f6', color: '#2d3436', border: 'none' }}
                            onClick={resetFilters}
                        >
                            Reset All
                        </Button>
                    </Col>
                </Row>
            </FilterCard>

            <LoadingOverlay
                active={loading}
                spinner={<BounceLoader color={'#f39c12'} />}
                text='Synchronizing with field production data...'
                styles={{
                    overlay: (base) => ({
                        ...base,
                        background: 'rgba(255, 255, 255, 0.9)',
                        zIndex: 10,
                        borderRadius: '16px'
                    })
                }}
            >
                {filteredHarvests.length === 0 && !loading ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No production logs found matching your criteria"
                        style={{ padding: '80px 0', background: '#fff', borderRadius: '16px', border: '1px dashed #ddd' }}
                    />
                ) : (
                    <>
                        <TableWrapper>
                            <BootstrapTable
                                keyField="harvest_id"
                                data={paginatedHarvests}
                                columns={columns}
                                hover
                                bordered={false}
                                className="modern-table"
                            />
                        </TableWrapper>

                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', paddingTop: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <span>
                                    <strong>
                                        Page {page} of {totalPages}
                                    </strong>
                                </span>
                                <select
                                    value={sizePerPage}
                                    onChange={e => {
                                        setSizePerPage(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    style={{
                                        padding: '5px 10px',
                                        borderRadius: '4px',
                                        border: '1px solid #d9d9d9',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                    }}
                                >
                                    {[5, 10, 25, 50, 100, 500, 1000, 2000, 3000, 5000].map(pageSizeOption => (
                                        <option key={pageSizeOption} value={pageSizeOption}>
                                            Show {pageSizeOption}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                                <button
                                    onClick={() => setPage(1)}
                                    disabled={page === 1}
                                    style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                                >
                                    {'<<'}
                                </button>
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                                >
                                    Previous
                                </button>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        value={page}
                                        min={1}
                                        max={totalPages}
                                        onChange={e => {
                                            const val = Number(e.target.value) || 1;
                                            setPage(Math.min(totalPages, Math.max(1, val)));
                                        }}
                                        style={{ width: '45px', textAlign: 'center', padding: '4px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                                    />
                                </div>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                    style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
                                >
                                    Next
                                </button>
                                <button
                                    onClick={() => setPage(totalPages)}
                                    disabled={page >= totalPages}
                                    style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
                                >
                                    {'>>'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </LoadingOverlay>

            {selectedItem && (
                <HarvestDetailModal
                    data={selectedItem}
                    onClose={() => setSelectedItem(null)}
                />
            )}

            <GenericExportCSVModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                data={filteredHarvests}
                columns={EXPORT_COLUMNS}
                title="Export Harvests to CSV"
                filenamePrefix="harvests_export"
            />
        </MainContainer>
    );
};

export default HarvestsTable;
